import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Modal, Select, Spin } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/Store";
import {
  createMergeRequest,
  fetchCreateMergeBrandDetails,
  fetchDuplicateBrand,
  fetchMergeRequestBrandList,
  resetCreateMergeRequestStatus,
  resetDuplicateBrandTable,
  resetMergeRequestBrandTable,
} from "../../../redux/LivePlatform/LivePlatformAction";
import { useAppDispatch } from "../../../redux/useAppDispatch";
import { validateNameLength } from "../../LivePlatformDetailPageComponent/BrandQualityCheckTab/BrandQualityCheckHelpers";

export interface CreateMergeRequestModalProps {
  close: () => void;
  openState: boolean;
  open: () => void;
  domain: string;
  setTabKey: () => void;
}

function CreateMergeRequestModal(props: CreateMergeRequestModalProps) {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [selectedPrimary, setSelectedPrimary] = useState<any>(null);

  useEffect(() => {
    if (props.openState && props.domain) {
      dispatch(fetchCreateMergeBrandDetails({ domain: props.domain }));
      setSelectedPrimary(null); // Reset primary on domain change
      form.resetFields();
      return () => {
        dispatch(resetCreateMergeRequestStatus());
      };
    }
  }, [props.openState, props.domain, dispatch, form]);

  const createMergeDuplicateBrandState = useSelector(
    (state: RootState) =>
      state.livePlatformState.duplicateBrandState.CreateMergeDuplicateBrandState
        .duplicateBrandDetails
  );
  const isLoading = useSelector(
    (state: RootState) =>
      state.livePlatformState.duplicateBrandState.CreateMergeDuplicateBrandState
        .loading
  );

  const createMergeRequestSubmitStatus = useSelector(
    (state: RootState) =>
      state.livePlatformState.duplicateBrandState.CreateMergeDuplicateBrandState
        .createMergeRequestStatus
  );

  // Set form fields when data is loaded
  useEffect(() => {
    if (props.openState && createMergeDuplicateBrandState) {
      form.setFieldsValue({
        website: createMergeDuplicateBrandState.website,
      });
    }
  }, [props.openState, createMergeDuplicateBrandState, form]);

  // Map brands to include key and value for labelInValue
  const primaryBrand =
    createMergeDuplicateBrandState?.duplicateBrands?.map((brandName: any) => ({
      label: brandName.value,
      value: brandName.key,
      key: brandName.key,
    })) || [];

  //Eliminates the selected primary brand from the secondary brand list
  const secondaryBrand =
    createMergeDuplicateBrandState?.duplicateBrands
      ?.filter((brandName: any) => brandName.key !== selectedPrimary?.value)
      .map((brandName: any) => ({
        label: brandName.value,
        value: brandName.key,
        key: brandName.key,
      })) || [];

  useEffect(() => {
    // Only set if a primary is selected
    if (selectedPrimary) {
      form.setFieldsValue({ SecondaryBrand: secondaryBrand });
    } else {
      form.setFieldsValue({ SecondaryBrand: [] });
    }
  }, [selectedPrimary, secondaryBrand, form]);

  const handleFinish = async (values: any) => {
    // Create payload with both Key and Value
    const primaryBrandObj = {
      Key: values.PrimaryBrand.value,
      Value: values.PrimaryBrand.label,
    };

    const secondaryBrandsArr = (values.SecondaryBrand || []).map(
      (item: any) => ({
        Key: item.value,
        Value: item.label,
      })
    );

    const result = await dispatch(
      createMergeRequest({
        DomainName: props.domain,
        primaryBrand: primaryBrandObj,
        SecondaryBrands: secondaryBrandsArr,
        Name: values.Name,
      })
    );

    if (createMergeRequest.fulfilled.match(result)) {
      props.close();
      form.resetFields();
      setSelectedPrimary(null);
      props.setTabKey();
      dispatch(resetDuplicateBrandTable());
      dispatch(resetMergeRequestBrandTable());
      dispatch(fetchDuplicateBrand({}));
      dispatch(fetchMergeRequestBrandList({}));
    }
  };
  const createMergeRequestModalContent = useMemo(() => {
    return (
      <div className="d-flex flex-column gap-4">
        <h4>Create Merge Request</h4>
        <Spin spinning={isLoading}>
          <Form
            form={form}
            name="createMergeRequestForm"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 24 }}
            style={{ maxWidth: 600 }}
            labelAlign={"left"}
            onFinish={handleFinish}
          >
            <Form.Item
              label="Domain"
              name="website"
              colon={false}
              required={false}
            >
              <Input readOnly={true} />
            </Form.Item>
            <Form.Item
              label="Name"
              name="Name"
              colon={false}
              rules={[
                {
                  required: true,
                  message: "Please provide name.",
                },
                {
                  validator: validateNameLength,
                },
              ]}
              validateTrigger="onBlur"
            >
              <Input
                onChange={() => {
                  dispatch(resetCreateMergeRequestStatus());
                }}
              />
            </Form.Item>
            <Form.Item
              label="Primary Brand"
              name="PrimaryBrand"
              colon={false}
              rules={[
                {
                  required: true,
                  message: "Please select primary brand.",
                },
              ]}
              validateTrigger="onBlur"
            >
              <Select
                size="middle"
                value={selectedPrimary}
                onChange={(option) => {
                  setSelectedPrimary(option);
                }}
                placeholder="Please select primary brand"
                style={{ width: "100%" }}
                options={primaryBrand}
                disabled={isLoading}
                labelInValue
              />
            </Form.Item>
            <Form.Item
              label="Secondary Brand"
              name="SecondaryBrand"
              colon={false}
              rules={[
                {
                  required: true,
                  message: "Please select secondary brand.",
                },
              ]}
              validateTrigger="onBlur"
            >
              <Select
                mode="multiple"
                size="middle"
                placeholder="Please select secondary brand"
                style={{ width: "100%" }}
                disabled={true}
                labelInValue
              />
            </Form.Item>
            {createMergeRequestSubmitStatus.error && (
              <div
                style={{ color: "red", marginBottom: 8, textAlign: "center" }}
              >
                {createMergeRequestSubmitStatus.error}
              </div>
            )}
            <div className="d-flex gap-3 justify-content-end">
              <Button
                className="saveButton"
                type="primary"
                htmlType="submit"
                disabled={isLoading}
                loading={createMergeRequestSubmitStatus.loading}
              >
                Merge
              </Button>
              <Button
                htmlType="reset"
                onClick={() => {
                  props.close();
                  form.resetFields();
                  setSelectedPrimary(null);
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Spin>
      </div>
    );
  }, [
    form,
    primaryBrand,
    secondaryBrand,
    selectedPrimary,
    props.close,
    isLoading,
  ]);
  return (
    <Modal
      centered
      zIndex={1500}
      footer={[]}
      width={600}
      onCancel={() => {
        props.close();
        form.resetFields();
        setSelectedPrimary(null);
      }}
      open={props.openState}
    >
      {createMergeRequestModalContent}
    </Modal>
  );
}

export default CreateMergeRequestModal;
